import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { ImReply } from "react-icons/im";
import { IoIosMore } from "react-icons/io";
import { getRelativeTime } from "../helpers/helper";
import { usePost } from "../../hooks/usePost";
import ReplySection from "../Reply/ReplySection";
import AddComments from "../AddComment/AddComment";
import Toast from "../../components/Toast/Toast";
import ReportDialog from "../Components/ReportDialog/ReportDialog";
import "./PostComments.scss";
import PostContent from "./PostContent";
import { CommentsService } from "../../services";
import { useUser } from "../../hooks/useUser";
import EditComment from "../EditComment/EditComment";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import Flex from "../../components/Flex/Flex";
import Button from "../../components/Button/Button";
import { revertProfileLinks } from "../../utils/revertProfileLinks";

const PostComments = ({ postId, post, isCommentingBlocked }) => {
    const currentUser = useSelector((state) => state.projectInfo.uid);
    const [newComment, setNewComment] = useState("");
    const [sortBy, setSortBy] = useState("New");
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [activeOptionsMenu, setActiveOptionsMenu] = useState(null);
    const [isReporting, setIsReporting] = useState(false);
    const [isCheckingReport, setIsCheckingReport] = useState(false);
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [commentToReport, setCommentToReport] = useState(null);
    const [showEditor, setShowEditor] = useState({}); // Track editor open state per comment
    const [mediaModal, setMediaModal] = useState({ open: false, media: null, type: null });
    const [editingComment, setEditingComment] = useState(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const commentId = searchParams.get("comment"); // "98384"
    const { users } = useUser();
    const commentRefs = useRef({});
    const [expandedCommentId, setExpandedCommentId] = useState(null);
    const [expandedReplies, setExpandedReplies] = useState([]);
    const hasHighlighted = useRef(false);
    // 1. State for paged comments and loaded pages
    const [pagedComments, setPagedComments] = useState({});
    const [loadedPages, setLoadedPages] = useState(new Set());
    const [minPage, setMinPage] = useState(null);
    const [maxPage, setMaxPage] = useState(null);
    const [hasMoreComments, setHasMoreComments] = useState(false);
    const replyId = searchParams.get("reply");
    const hasExpandedReply = useRef(false);
    const projectInfo = useSelector((state) => state.projectInfo);

    // 2. Helper to flatten comments for rendering
    const getAllComments = () => {
        const pages = Array.from(loadedPages).sort((a, b) => a - b);
        return pages.flatMap(page => pagedComments[page] || []);
    };

    const { updateCommentCountInPost } = usePost();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setActiveOptionsMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleOptionsMenu = (commentId) => {
        setActiveOptionsMenu(activeOptionsMenu === commentId ? null : commentId);
    };

    const fetchComments = async (page = 1, sort = sortBy === "New" ? "Latest" : "Popular", prepend = false) => {
        if (!postId) return;
        try {
            const params = { page, sort };
            if (commentId) params.comment_id = commentId;
            const response = await CommentsService.getCommentsByPostId(postId, params);
            if (response.data.success) {
                const page = response.data.page;
                /**
                 * Remove revertProfileLinks when mobile is version is released[]}
                 */
                const comments = response.data.data.map(comment => ({
                    ...comment,
                    content: revertProfileLinks(comment.content)
                }));
                setPagedComments({ [page]: comments });
                setLoadedPages(new Set([page]));
                setMinPage(page);
                setMaxPage(page);
                setHasMoreComments(response.data.data.length >= response.data.limit);
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
        }
    };

    useEffect(() => {
        if (post) {
            fetchComments(1).catch(err=>{
                console.error(err)
            })
        }
    }, [post, postId, sortBy, commentId, location]);

    const deleteComment = useCallback(async (commentId) => {
        try {
            // Use CommentsService to delete the comment
            const response = await CommentsService.deleteComment(commentId, postId);
            if (response?.data?.success) {
                setPagedComments(prev => {
                    const newPaged = { ...prev };
                    Object.keys(newPaged).forEach(page => {
                        newPaged[page] = newPaged[page].filter(item => item.id !== commentId);
                    });
                    return newPaged;
                });
                // Update comment count in post
                const currentCommentCount = post.comment_count || 0;
                if (currentCommentCount > 0) {
                    await updateCommentCountInPost(postId, false);
                }
                setToast({ show: true, message: "Comment deleted successfully", type: "success" });
            } else {
                setToast({ show: true, message: "Failed to delete comment", type: "error" });
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
            setToast({ show: true, message: "Error deleting comment", type: "error" });
        }
    }, [postId, updateCommentCountInPost, post]);

    const toggleLikeCommentOrReplies = async (commentId, isLiked) => {
        try {
            await CommentsService.toggleCommentLike(commentId, isLiked ? 0 : 1);
            setPagedComments(prev => {
                const newPaged = { ...prev };
                Object.keys(newPaged).forEach(page => {
                    newPaged[page] = newPaged[page].map(item =>
                        item.id === commentId
                            ? {
                                ...item,
                                user_has_liked: !isLiked,
                                likes_count: isLiked
                                    ? Number(item.likes_count) - 1
                                    : Number(item.likes_count) + 1
                            }
                            : item
                    );
                });
                return newPaged;
            });
            // Update expandedReplies if present
            setExpandedReplies((prev) =>
                prev.map((reply) =>
                    reply.id === commentId
                        ? { ...reply, user_has_liked: !isLiked, likes_count: reply.user_has_liked ? Number(reply.likes_count) - 1 : Number(reply.likes_count) + 1 }
                        : reply
                )
            );
        } catch (error) {
            console.error("Error toggling like:", error);
        }
    };
    const handleShowReply = async (commentId, replyID) => {
        if (expandedCommentId === commentId) {
            // Collapse if already open
            setExpandedCommentId(null);
            setExpandedReplies([]);
            return;
        }
        setExpandedReplies([]);
        setLoadingReplies(true);
        setExpandedCommentId(commentId);
        const response = await CommentsService.getCommentReplies(commentId,{
            reply_id: replyID
        });
        /**
         * Remove revertProfileLinks when mobile is version is released[]}
         */
        const formattedReplies = (response.data.data || []).map(reply => ({
            ...reply,
            content: revertProfileLinks(reply.content)
        }));
        setExpandedReplies(formattedReplies || []);
        setLoadingReplies(false);
    };

    const addMoreReplies = useCallback((commentID,newReplies) => {
        setExpandedReplies((prev) => [...prev, ...newReplies]);
    }, []);

    const onAddReply = useCallback((commentId, replyId, replyData, updateFn) => {
        try {
            setPagedComments(prev => {
                const newPaged = { ...prev };
                Object.keys(newPaged).forEach(page => {
                    newPaged[page] = newPaged[page].map(item => {
                        if (item.id === commentId) {
                            let prevReplies = Array.isArray(item.replies) ? item.replies : [];
                            let newReplies, newRepliesCount;
                            if (typeof updateFn === "function") {
                                newReplies = updateFn(prevReplies, item);
                                newRepliesCount = item.replies_count;
                                if (newReplies.length < prevReplies.length) {
                                    newRepliesCount = Math.max(0, (Number(item.replies_count) || 0) - 1);
                                }
                            } else if (replyData) {
                                newReplies = [
                                    ...prevReplies,
                                    { ...replyData, id: replyId }
                                ];
                                newRepliesCount = (Number(item.replies_count) || 0) + 1;
                            } else {
                                return item;
                            }
                            return { ...item, replies: newReplies, replies_count: newRepliesCount };
                        }
                        return item;
                    });
                });
                return newPaged;
            });
            // Update expandedReplies if the expanded comment matches
            if (expandedCommentId === commentId) {
                if (typeof updateFn === "function") {
                    setExpandedReplies((prev) => updateFn(prev, { id: commentId }));
                } else if (replyData) {
                    setExpandedReplies((prev) => [
                        { ...replyData, id: replyId },
                        ...prev
                    ]);
                }
            }
        } catch (error) {
            console.error("Error updating reply:", error);
        }
    }, [expandedCommentId]);
    const addComment = async (media) => {
        if (!newComment.trim() && !Object.keys(media)?.length) return;

        try {
            const commentData = {
                post_id: postId,
                comment_id: null,
                content: newComment,
                ...(media?.image && { image: media.image }),
                ...(media?.gif && { gif: media.gif }),
            };
            const response = await CommentsService.createComment(commentData);

            await updateCommentCountInPost(postId, true);

            setPagedComments(prev => {
                const firstPage = Math.min(...Object.keys(prev).map(Number));
                return {
                    ...prev,
                    [firstPage]: [
                        {
                            ...commentData,
                            created_at: new Date().toISOString(),
                            id: response.data.data.id,
                            image_url: commentData.image ? commentData.image.url : undefined,
                            gif_url: commentData.gif ? commentData.gif.url : undefined,
                            user_id: currentUser,
                            user: {
                                user_id: Number(currentUser),
                                user_name: users[currentUser]?.user_name,
                                user_image: users[currentUser]?.user_image,
                            }
                        },
                        ...(prev[firstPage] || [])
                    ]
                };
            });
            setNewComment("");
        } catch (error) {
            console.error("Error adding comment:", error);
        }
    };

    const handleSortSelection = async (option) => {
        setSortBy(option);
        await fetchComments(1, option === "new" ? "latest" : "popular");
        setShowSortDropdown(false);
    };

    const navigateToProfile = (userId) => {
        navigate(`/community/profiles/${userId || "unknown"}`);
    };

    const loadMoreComments = async () => {
        let nextPage = maxPage + 1;
        let prevPage = minPage - 1;
        let fetchPrev = !loadedPages.has(1) && prevPage >= 1;
        let fetchNext = hasMoreComments;

        const requests = [];
        if (fetchPrev) requests.push(CommentsService.getCommentsByPostId(postId, { page: prevPage, sort: sortBy === "New" ? "Latest" : "Popular", comment_id: commentId}));
        if (fetchNext) requests.push(CommentsService.getCommentsByPostId(postId, { page: nextPage, sort: sortBy === "New" ? "Latest" : "Popular", comment_id: commentId }));

        const results = await Promise.all(requests);

        let newPagedComments = { ...pagedComments };
        let newLoadedPages = new Set(loadedPages);

        results.forEach((res, idx) => {
            if (res.data.success && res.data.data.length > 0) {
                const page = fetchPrev && idx === 0 ? prevPage : nextPage;
                /**
                 * Remove revertProfileLinks when mobile is version is released[]}
                 */
                newPagedComments[page] = res.data.data.map(comment => ({
                    ...comment,
                    content: revertProfileLinks(comment.content)
                }));
                newLoadedPages.add(page);
                if (page < minPage) setMinPage(page);
                if (page > maxPage) setMaxPage(page);
                setHasMoreComments(res.data.data.length >= res.data.limit);
            } else if (fetchNext && idx === (fetchPrev ? 1 : 0)) {
                setHasMoreComments(false); // Stop if next page is empty
            }
        });

        setPagedComments(newPagedComments);
        setLoadedPages(newLoadedPages);
    };

    const _comments = getAllComments();

    // 2. In useEffect, after fetching comments, expand the parent comment if replyId exists
    useEffect(() => {
        if (commentId && replyId && _comments.length > 0 && !hasExpandedReply.current) {
            // Find the parent comment
            const parentComment = _comments.find(c => String(c.id) === String(commentId));
            if (parentComment) {
                // Expand replies for this comment
                handleShowReply(parentComment.id, replyId).catch((err)=>{
                    console.error(err)
                })
                hasExpandedReply.current = true;
            }
        }
        // eslint-disable-next-line
    }, [commentId, replyId, _comments]);

    const handleEditComment = (comment) => {
        setEditingComment(comment);
        setActiveOptionsMenu(null);
    };

    const handleCancelEdit = () => {
        setEditingComment(null);
    };

    const handleCommentUpdated = (updatedComment) => {
        // Find the page containing the comment
        let found = false;
        const newPagedComments = { ...pagedComments };
        Object.keys(newPagedComments).forEach(page => {
            newPagedComments[page] = newPagedComments[page].map(c => {
                if (c.id === updatedComment.id) {
                    found = true;
                    return updatedComment;
                }
                return c;
            });
        });
        if (found) {
            setPagedComments(newPagedComments);
            setEditingComment(null);
        }
    };


    // Function to handle comment reporting - uses API to check/report
    const handleReportComment = async (commentId) => {
        setIsCheckingReport(true);
        setActiveOptionsMenu(null);
        setCommentToReport(commentId);
        setShowReportDialog(true);
        setIsCheckingReport(false);


    };

    // Function to handle report submission from dialog
    const handleReportSubmit = async (reason) => {
        if (!commentToReport) return;

        try {
            setIsReporting(true);
            const response = await CommentsService.reportComment(commentToReport, reason);
            setShowReportDialog(false);
            setCommentToReport(null);

            if (response?.data?.success) {
                setToast({
                    show: true,
                    message: "Comment has been reported successfully",
                    type: "success"
                });
            } else {
                setToast({
                    show: true,
                    message: response?.data?.message || "Failed to report comment. Please try again.",
                    type: "error"
                });
            }
        } catch (error) {
            if (error.response.data?.alreadyReported) {
                setToast({
                    show: true,
                    message: "You have already reported this comment.",
                    type: "warning"
                });
            } else {
                setToast({
                    show: true,
                    message: "Failed to report comment. Please try again.",
                    type: "error"
                });
            }
        } finally {
            setIsReporting(false);
        }
    };

    // Prevent background scroll when mediaModal is open
    useEffect(() => {
        if (mediaModal.open) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [mediaModal.open]);

    // 2. Scroll and highlight when commentId changes
    useEffect(() => {
        if (hasHighlighted.current) return;
        if (commentId && commentRefs.current[commentId]) {
            commentRefs.current[commentId].scrollIntoView({ behavior: "smooth", block: "center" });
            // commentRefs.current[commentId].classList.add("highlight-comment");
            // Remove highlight after 2s
            // setTimeout(() => {
            //     commentRefs.current[commentId]?.classList.remove("highlight-comment");
            // }, 2000);
            hasHighlighted.current = true; // Mark as done
        }
    },[commentId, _comments]);

    return (
        <div className="comments-section">
            {!isCommentingBlocked && (
                <AddComments
                    postId={postId}
                    commentText={newComment}
                    addComment={addComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    showEditor={!!showEditor['main']}
                    onInputFocus={() => setShowEditor(prev => ({ ...prev, main: true }))}
                    onCancel={() => {
                        setShowEditor(prev => ({ ...prev, main: false }))
                        setNewComment("");
                    }}
                />
            )}

            <div className="comment-header">
                <div className="comment-title">
                    <h3>Comments</h3>
                </div>
                {(_comments?.length > 0 && projectInfo?.pubid !== "50134") && (
                    <div className="comment-sort">
                        <div className="sort-dropdown">
                            <button
                                className="sort-button"
                                onClick={() => setShowSortDropdown(!showSortDropdown)}
                            >
                                Sorted by {sortBy === "popular" ? "Popular" : "New"} ▼
                            </button>
                            {showSortDropdown && (
                                <div className="sort-dropdown-content">
                                    <div
                                        className={`sort-option ${sortBy === "popular" ? "active" : ""}`}
                                        onClick={() => handleSortSelection("popular")}
                                    >
                                        Popular {sortBy === "popular" && "✓"}
                                    </div>
                                    <div
                                        className={`sort-option ${sortBy === "new" ? "active" : ""}`}
                                        onClick={() => handleSortSelection("new")}
                                    >
                                        New {sortBy === "new" && "✓"}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <Flex className="comments-container" direction={"column"} gap={8}>
                {_comments.map((comment) => {
                    return (
                        <div key={comment?.id} className="comment-item"  ref={el => commentRefs.current[comment.id] = el}>
                            <div className="comment-wrapper">
                                <div className="comment-header">
                                    <div className="author-avatar">
                                        <img
                                            src={comment.user?.user_image || "https://gizmeon.mdc.akamaized.net/PUB-50119/post/1749720612786.png"}
                                            alt={comment.user?.user_name}
                                            onClick={() => navigateToProfile(comment.user.user_id)}
                                        />
                                    </div>
                                    <div className="author-info">
                                        <span className="author-name" onClick={() => navigateToProfile(comment.user.user_id)}>
                                            {comment?.user.user_name || "Unknown User"}
                                        </span>
                                    </div>
                                    <div className="comment-meta">
                                        <span className="comment-date">
                                            {getRelativeTime(new Date(comment?.created_at))}
                                        </span>
                                        <div className="options-wrapper" ref={activeOptionsMenu === comment.id ? menuRef : null}>
                                            <IoIosMore
                                                className="more-icon"
                                                onClick={() => toggleOptionsMenu(comment.id)}
                                            />
                                            {activeOptionsMenu === comment.id && (
                                                <div className="options-menu">
                                                    {comment.user.user_id === Number(currentUser) && (
                                                        <>
                                                            <button onClick={() => handleEditComment(comment)}>
                                                                <FiEdit2 className="comment-icon" />
                                                                Edit
                                                            </button>
                                                            <button onClick={() => deleteComment(comment.id)}>
                                                                <RiDeleteBin6Line className="comment-icon" />
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                    {comment.user.user_id !== Number(currentUser) && (
                                                        <button
                                                            onClick={() => handleReportComment(comment.id)}
                                                            disabled={isCheckingReport || isReporting}
                                                        >
                                                            {isCheckingReport ? "Checking..." : "Report"}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="comment-content">
                                    {editingComment && editingComment.id === comment.id ? (
                                        <EditComment
                                            comment={editingComment}
                                            onCancel={handleCancelEdit}
                                            onCommentUpdated={handleCommentUpdated}
                                        />
                                    ) : (
                                        <div className="comment-text">
                                            <PostContent content={comment.content} className="comment-post-content" />
                                            {(comment.gif_url || comment?.image_url) && (
                                                <div className="comment-media-container" style={{
                                                    marginTop: '10px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '8px',
                                                    padding: '8px',
                                                    borderRadius: '8px'
                                                }}>
                                                    {comment.gif_url && (
                                                        <div className="comment-media comment-gif" onClick={() => setMediaModal({ open: true, media: comment.gif_url, type: 'gif' })} style={{ cursor: 'pointer' }}>
                                                            <img
                                                                src={comment.gif_url}
                                                                alt=""
                                                                style={{
                                                                    maxWidth: '220px',
                                                                    maxHeight: '180px',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '8px',
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                    {comment?.image_url && (
                                                        <div className="comment-media comment-image" onClick={() => setMediaModal({ open: true, media: comment.image_url, type: 'image' })} style={{ cursor: 'pointer' }}>
                                                            <img
                                                                src={comment.image_url}
                                                                alt=""
                                                                style={{
                                                                    maxWidth: '240px',
                                                                    maxHeight: '240px',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '8px',
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <Flex justify="space-between">
                                        <Button
                                            type={"borderless"}
                                            size={"medium"}
                                            className="pd-0"
                                            onClick={() =>
                                                toggleLikeCommentOrReplies(comment.id, comment?.user_has_liked)
                                            }
                                        >
                                            <Flex gap={4} align="center" justify={"center"}>
                                                {comment?.user_has_liked ? (
                                                    <FaHeart style={{ fill: "#e91e63" }} />
                                                ) : (
                                                    <FaRegHeart />
                                                )}
                                                <span className="like-count">
                                                    {comment.likes_count > 0 ? comment.likes_count : ""}
                                                </span>
                                            </Flex>
                                        </Button>
                                        <Button size={"small"} className="pd-0" type={"borderless"} onClick={() => handleShowReply(comment.id, replyId)}>
                                            <Flex gap={4} align="center" justify={"center"}>
                                                <ImReply />
                                                <span className="reply-count">
                                                    {comment.replies_count > 0 ? comment.replies_count : ""}
                                                </span>
                                            </Flex>
                                        </Button>
                                    </Flex>
                                </div>
                                {expandedCommentId === comment.id && (
                                    <ReplySection
                                        postId={postId}
                                        commentId={comment.id}
                                        showReply={true}
                                        replies={expandedReplies}
                                        onAddReply={onAddReply}
                                        currentUser={currentUser}
                                        isLoading={loadingReplies}
                                        isCommentingBlocked={isCommentingBlocked || post?.commentingblocked}
                                        showEditor={!!showEditor[comment.id]}
                                        onInputFocus={() => setShowEditor(prev => ({...prev, [comment.id]: true}))}
                                        onCancel={() => setShowEditor(prev => ({...prev, [comment.id]: false}))}
                                        repliesCount={comment.replies_count}
                                        addMoreReplies={addMoreReplies}
                                    />)}
                            </div>
                        </div>
                    )
                })}
            </Flex>

            {/* Load More button: only increases displayCount, no extra fetch */}
            {hasMoreComments && (
                <button
                    className="load-more-btn"
                    onClick={loadMoreComments}
                >
                    Load More Comments
                </button>
            )}

            {/* Report Dialog */}
            <ReportDialog
                isOpen={showReportDialog}
                onClose={() => setShowReportDialog(false)}
                onSubmit={handleReportSubmit}
                isSubmitting={isReporting}
                type="comment"
            />

            {/* Toast notification */}
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                    autoClose={3000}
                />
            )}

            {/* Media Modal for full screen view */}
            {mediaModal.open && (
                <div className="media-overlay" onClick={() => setMediaModal({ open: false, media: null, type: null })}>
                    <div className="overlay-background"></div>
                    <div className="expanded-media-container" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
                        <button
                            className="close-button"
                            style={{ position: 'fixed', top: 24, right: 32, zIndex: 1001, fontSize: 36, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setMediaModal({ open: false, media: null, type: null })}
                            aria-label="Close fullscreen media"
                        >
                            ×
                        </button>
                        <div className="expanded-media-wrapper">
                            {mediaModal.type === 'image' && (
                                <img src={mediaModal.media} alt="" className="expanded-media" />
                            )}
                            {mediaModal.type === 'gif' && (
                                <img src={mediaModal.media} alt="" className="expanded-media" />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PostComments;